import React from 'react';
import './Witstorms.css';

const logoSrc = '/logo.jpg';
const productScreenshot = require('./screenshots/scc2.PNG');

export default function Witstorms({ onNavigateToResearch }) {
  // Handle "Get Started" button click.
  const handleGetStarted = () => {
    console.log('Get Started button clicked');
    window.open('https://app.witstorms.com/', '_blank');
  };

  return (
    <div className="font-sans text-gray-100 bg-animated min-h-screen">
      {/* Header with logo and navigation links */}
      <header className="header">
        <nav className="nav">
          <div className="logo-container">
            <img src={logoSrc} alt="Witstorms Intelligence Logo" className="logo-image" />
            <span className="logo-text">Witstorms Intelligence</span>
          </div>
          <div className="nav-links">
            <a href="#features" className="nav-link">Features</a>
            <a href="#solution" className="nav-link">Solution</a>
            <a href="#contact" className="nav-link">Contact</a>
            <button
              type="button"
              className="nav-link nav-link-button"
              onClick={() => onNavigateToResearch && onNavigateToResearch()}
            >
              Research
            </button>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-badge">
            <span className="badge-text">Powered by WitstormsAI Research Labs</span>
          </div>
          <h1 className="hero-title">AI-Powered Clinical Decision Support System for Evidence-Based Medicine</h1>
          <p className="hero-text">
            Advanced AI and natural language processing deliver real-time clinical intelligence through a unified medical literature corpus, 
            reducing medical errors and improving patient outcomes.
          </p>
          
          <div className="hero-buttons">
            <button onClick={handleGetStarted} className="hero-button primary">
              Get Started Free
            </button>
          </div>

          <div className="hero-stats">
            <div className="stat-item">
              <span className="stat-number">99.7%</span>
              <span className="stat-label">Accuracy Rate</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">10K+</span>
              <span className="stat-label">Medical References</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">500+</span>
              <span className="stat-label">Healthcare Professionals</span>
            </div>
          </div>
        </div>
      </section>

      {/* Product Preview Section */}
      <section className="preview-section">
        <div className="container">
          <div className="section-header preview-header">
            <h2 className="section-title">Clinical Workflow, Elevated</h2>
            <p className="section-subtitle">
              A focused interface designed to deliver treatment intelligence with speed, clarity, and clinical confidence.
            </p>
          </div>

          <div className="preview-frame">
            <img
              src={productScreenshot}
              alt="Witstorms Intelligence clinical decision support interface"
              className="preview-image"
            />
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="problem-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">The Problem</h2>
            <div className="problem-content">
              <p className="problem-text">
                Medical professionals struggle with fragmented information sources, complex search interfaces, and limited access to current medical literature. 
                Traditional systems lack the intelligence to provide context-aware recommendations, leading to prescription errors and suboptimal patient care. 
                Traditional EMR systems lack the intelligence to research real-time information and provide detailed output with tailored real-time context.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section id="solution" className="solution-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">The Solution</h2>
            <div className="solution-content">
              <p className="solution-text">
                Our AI-powered clinical decision support system uses advanced natural language processing to provide 
                unified access to approved clinical literature, documents, references, and online sources with context-aware, evidence-based recommendations that reduce errors and improve clinical outcomes.
              </p>
              
              <div className="solution-details">
                <div className="solution-detail">
                  <h3 className="solution-detail-title">Unified Medical Literature Corpus</h3>
                  <p className="solution-detail-text">
                    We have built a comprehensive unified medical literature corpus that integrates approved clinical literature, documents, references, and online sources to address the information fragmentation problem. Our natural language processing capabilities enable seamless access to this unified knowledge base, providing healthcare professionals with comprehensive, evidence-based information in real-time.
                  </p>
                </div>
                
                <div className="solution-detail">
                  <h3 className="solution-detail-title">Context Engineering Approach</h3>
                  <p className="solution-detail-text">
                    Our AI system integrates multiple data sources to provide comprehensive clinical context: patient medical history, current presenting problems, real-time clinical research findings, and available medical literature. This holistic approach ensures recommendations are personalized, evidence-based, and clinically relevant.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI/ML Capabilities Section */}
      <section className="ai-capabilities-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Core AI/ML Capabilities</h2>
            <p className="section-subtitle">
              Advanced artificial intelligence and machine learning powering intelligent clinical decisions
            </p>
          </div>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                </svg>
              </div>
              <h3 className="feature-title">Natural Language Processing</h3>
              <p className="feature-description">
                Query medical literature in plain English with conversational AI that understands clinical context.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <path d="M9 12l2 2 4-4"/>
                </svg>
              </div>
              <h3 className="feature-title">Unified Medical Literature Corpus</h3>
              <p className="feature-description">
                Integrated access to approved clinical literature, documents, references, and online sources in one comprehensive knowledge base.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <path d="M9 12l2 2 4-4"/>
                </svg>
              </div>
              <h3 className="feature-title">AI-Powered Error Detection</h3>
              <p className="feature-description">
                Proactive identification of potential prescription errors using advanced pattern recognition.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <circle cx="12" cy="12" r="6"/>
                  <circle cx="12" cy="12" r="2"/>
                </svg>
              </div>
              <h3 className="feature-title">Context Engineering</h3>
              <p className="feature-description">
                Our AI system integrates multiple data sources to provide comprehensive clinical context: patient medical history, 
                current presenting problems, real-time clinical research findings, and available medical literature. 
                This holistic approach ensures recommendations are personalized, evidence-based, and clinically relevant.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                </svg>
              </div>
              <h3 className="feature-title">Real-Time Learning</h3>
              <p className="feature-description">
                System improves with each clinical interaction, becoming more accurate and personalized over time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Strengths Section */}
      <section className="technical-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Technical Strengths</h2>
            <p className="section-subtitle">
              Built on cutting-edge AI infrastructure for reliable, scalable clinical intelligence
            </p>
          </div>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                </svg>
              </div>
              <h3 className="feature-title">Advanced AI Architecture</h3>
              <p className="feature-description">
                Built on modern natural language processing frameworks with advanced AI capabilities.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <path d="M9 12l2 2 4-4"/>
                </svg>
              </div>
              <h3 className="feature-title">Vector-Based Search</h3>
              <p className="feature-description">
                Semantic understanding of medical literature with intelligent document retrieval.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <path d="M9 12l2 2 4-4"/>
                </svg>
              </div>
              <h3 className="feature-title">Contextual Intelligence</h3>
              <p className="feature-description">
                AI that understands clinical scenarios and patient context for relevant recommendations.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <circle cx="12" cy="12" r="6"/>
                  <circle cx="12" cy="12" r="2"/>
                </svg>
              </div>
              <h3 className="feature-title">Real-Time Processing</h3>
              <p className="feature-description">
                Instant access to current medical literature and evidence with sub-second response times.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                </svg>
              </div>
              <h3 className="feature-title">Scalable AI Infrastructure</h3>
              <p className="feature-description">
                Cloud-native architecture for reliable performance and seamless scaling.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section id="features" className="features-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Key Features</h2>
            <p className="section-subtitle">
              Comprehensive AI-powered clinical decision support for healthcare professionals
            </p>
          </div>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                </svg>
              </div>
              <h3 className="feature-title">Unified Medical Literature Access</h3>
              <p className="feature-description">
                AI-powered search across BNF, BNFC, and pharmacopoeia with intelligent document retrieval.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <path d="M9 12l2 2 4-4"/>
                </svg>
              </div>
              <h3 className="feature-title">Natural Language Queries</h3>
              <p className="feature-description">
                Ask complex clinical questions in conversational language with AI-powered understanding.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <path d="M9 12l2 2 4-4"/>
                </svg>
              </div>
              <h3 className="feature-title">Context-Aware Recommendations</h3>
              <p className="feature-description">
                AI considers patient history, current medications, and clinical context for personalized guidance.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <circle cx="12" cy="12" r="6"/>
                  <circle cx="12" cy="12" r="2"/>
                </svg>
              </div>
              <h3 className="feature-title">Proactive Error Prevention</h3>
              <p className="feature-description">
                AI algorithms identify potential prescription errors before they occur.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                </svg>
              </div>
              <h3 className="feature-title">Evidence-Based Decision Support</h3>
              <p className="feature-description">
                Real-time access to current medical literature and clinical guidelines with AI-powered insights.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section className="audience-section">
        <div className="container">
          <div className="audience-content">
            <h2 className="audience-title">Designed for Healthcare Professionals</h2>
            <p className="audience-text">
              Pharmacists, physicians, nurses, and clinical staff who need advanced AI-powered clinical decision support 
              with natural language processing capabilities.
            </p>
            <div className="audience-badges">
              <span className="audience-badge">Pharmacists</span>
              <span className="audience-badge">Physicians</span>
              <span className="audience-badge">Nurses</span>
              <span className="audience-badge">Clinical Staff</span>
            </div>
          </div>
          </div>
      </section>

      {/* Nick Bostrom Quote Section */}
      <section className="quote-section">
        <div className="quote-container">
          <div className="quote-banner">
            <div className="quote-strip">
            <div className="quote-strip-inner">
              "Machine intelligence is the last invention that humanity will ever need to make." - Nick Bostrom
              </div>
            </div>
          </div> 
        </div>
      </section>

      {/* Ready to Prevent Section */}
      <section className="ready-section">
        <div className="container">
          <div className="ready-content">
            <div className="ready-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12l2 2 4-4"/>
                <path d="M21 12c.552 0 1-.448 1-1V8c0-.552-.448-1-1-1h-4c-.552 0-1 .448-1 1v3c0 .552.448 1 1 1h4z"/>
                <path d="M3 12c-.552 0-1-.448-1-1V8c0-.552.448-1 1-1h4c.552 0 1 .448 1 1v3c0 .552-.448 1-1 1H3z"/>
                <path d="M12 21c.552 0 1-.448 1-1v-4c0-.552-.448-1-1-1s-1 .448-1 1v4c0 .552.448 1 1 1z"/>
                <path d="M12 9c.552 0 1-.448 1-1V4c0-.552-.448-1-1-1s-1 .448-1 1v4c0 .552.448 1 1 1z"/>
              </svg>
                </div>
            <h2 className="ready-title">Experience the Future of Clinical Decision Support</h2>
            <p className="ready-description">
              Join healthcare professionals who are transforming patient care through advanced natural language processing and unified medical literature access. 
              Experience the future of clinical decision support with AI-powered intelligence.
            </p>
            <button onClick={handleGetStarted} className="ready-button">
              Request Early Access
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <div className="footer-logo">
                <img src={logoSrc} alt="Witstorms Intelligence Logo" className="footer-logo-image" />
                <span className="footer-logo-text">Witstorms Intelligence</span>
              </div>
              <p className="footer-description">
                Empowering healthcare professionals with AI-powered prescription error prevention 
                and evidence-based medical decision support.
              </p>
            </div>
            
            <div className="footer-section">
              <h4 className="footer-title">Contact</h4>
              <p className="footer-email">witstorms@gmail.com</p>
              <p className="footer-address">
                Powered by WitstormsAI Research Labs
              </p>
            </div>
      </div>

          <div className="footer-bottom">
            <p className="footer-copyright">
              © 2024 Witstorms Intelligence. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}